import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import { InactivityHandler } from "@components/InactivityHandler";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ButtonDownloadTablaAmortizacion from "@components/ButtonDownloadTablaAmortizacion";
import { CustomSwitch } from "@components/CustomSwitch";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import { useLazyGetMotorbikeDetailByIdQuery, usePostValidateNextStageMutation } from "@api/applications";
import { formatCurrency, weeksToMonths } from "@helpers/funciones";
import CenteredLoader from "@components/CenteredLoader";
import useStatusConfig from "@hooks/useStatusConfig";

type FinancialRowItemProps = {
  label: string;
  value: number | string;
  valueSuffix?: string;
  isMobileOrTablet?: boolean;
};

type CreditConditionsProps = {
  credit_amount: number;
  credit_term: number;
  down_payment_percentage: number;
  down_payment_amount: number;
};

const SCREEN_STATUS = "CSBAF";

const FinancialRowItem = ({ label, value, valueSuffix, isMobileOrTablet }: FinancialRowItemProps) => {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography variant={isMobileOrTablet ? "body2" : "body1"} color="primary.dark" fontWeight={400}>
        {label}
      </Typography>
      <Typography variant={isMobileOrTablet ? "body2" : "body1"} color="primary.dark" fontWeight={600}>
        {value}
        {valueSuffix && (
          <Typography
            component="span"
            variant={isMobileOrTablet ? "body3" : "body2"}
            color="primary.dark"
            fontWeight={500}
          >
            {" "}
            {valueSuffix}
          </Typography>
        )}
      </Typography>
    </Box>
  );
};

const BienvenidaFirmaPF = () => {
  // hooks
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);

  // redux hooks
  const application = useSelector((state: RootState) => state.register.application);
  const motorbikeInfo = useSelector((state: RootState) => state.register.motorbike);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [getMotorbikeDetailById] = useLazyGetMotorbikeDetailByIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  // state
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authorizeChecked, setAuthorizeChecked] = useState(false);
  const [creditConditions, setCreditConditions] = useState<CreditConditionsProps>({
    credit_amount: 0,
    credit_term: 0,
    down_payment_percentage: 0,
    down_payment_amount: 0,
  });

  const handleContinue = async () => {
    try {
      if (!authorizeChecked) {
        return;
      }

      if (!nextStatus) {
        throw new Error("Next status is required");
      }

      setIsSubmitting(true);

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `BOFU - ${accountUser.email}`,
      }).unwrap();

      navigate(nextStatus.url);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "BienvenidaFirmaPF-handleContinue",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchInitialData = useCallback(async () => {
    try {
      if (!application.id) {
        throw new Error("Application ID is required");
      }

      if (!motorbikeInfo.motorbikeDetailId) {
        throw new Error("Motorbike detail ID is required");
      }

      setIsLoading(true);

      const motorbikeDetailResponse = await getMotorbikeDetailById({
        solicitudId: application.id,
        motorbikeDetailId: motorbikeInfo.motorbikeDetailId,
      }).unwrap();

      const { approved_details } = motorbikeDetailResponse;
      if (!approved_details) {
        throw new Error("Approved details not found");
      }

      const { authorized_period, amount_financed, down_payment } = approved_details;

      const credit_term = weeksToMonths(authorized_period || 0);
      const down_payment_amount = (amount_financed * down_payment) / 100;

      setCreditConditions({
        credit_term,
        credit_amount: amount_financed,
        down_payment_percentage: down_payment,
        down_payment_amount: down_payment_amount,
      });

      setIsLoading(false);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "BienvenidaFirmaPF-fetchInitialData",
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [application.id, dispatch, getMotorbikeDetailById, motorbikeInfo.motorbikeDetailId]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {isLoading ? (
            <CenteredLoader />
          ) : (
            <>
              <Typography variant={"tofu_title"} textAlign="center" color={"primary.main"}>
                Información esencial antes de firmar
              </Typography>

              <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 3 : 4, textAlign: "center" }}>
                <Typography
                  sx={{ color: "primary.dark", mb: 1 }}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  textAlign="center"
                  fontWeight={500}
                >
                  Antes de proceder a la firmar, visualiza este video con la información esencial de tu crédito y acepta
                  sus condiciones finales
                </Typography>

                <Divider sx={{ my: 2, borderColor: "primary.light", borderBottomWidth: "0.5" }} />

                <Typography
                  sx={{ color: "primary.dark", mt: isMobileOrTablet ? 2 : 4 }}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  textAlign="center"
                  fontWeight={500}
                >
                  Condiciones del Crédito
                </Typography>

                <Box sx={{ textAlign: "left", mt: 2 }}>
                  <Box flex={1} display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                    <FinancialRowItem
                      label="Monto del Crédito"
                      value={formatCurrency(creditConditions.credit_amount)}
                      valueSuffix=" MXN"
                      isMobileOrTablet={isMobileOrTablet}
                    />
                    <Divider orientation="vertical" flexItem sx={{ borderColor: "primary.main", borderWidth: "0.5" }} />
                    <FinancialRowItem
                      label="Plazo"
                      value={creditConditions.credit_term}
                      valueSuffix=" meses"
                      isMobileOrTablet={isMobileOrTablet}
                    />
                  </Box>
                </Box>

                <Typography
                  sx={{ color: "primary.main", mt: isMobileOrTablet ? 2 : 4 }}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  textAlign="center"
                  fontWeight={500}
                >
                  Enganche
                </Typography>

                <Box sx={{ textAlign: "left", mt: 2 }}>
                  <Box flex={1} display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                    <FinancialRowItem
                      label="Enganche Porcentual"
                      value={creditConditions.down_payment_percentage}
                      valueSuffix=" %"
                      isMobileOrTablet={isMobileOrTablet}
                    />
                    <Divider orientation="vertical" flexItem sx={{ borderColor: "primary.main", borderWidth: "0.5" }} />
                    <FinancialRowItem
                      label="Valor Enganche"
                      value={formatCurrency(creditConditions.down_payment_amount)}
                      valueSuffix=" MXN"
                      isMobileOrTablet={isMobileOrTablet}
                    />
                  </Box>
                </Box>

                <ButtonDownloadTablaAmortizacion
                  sx={{ mt: isMobileOrTablet ? 4 : 6 }}
                  disabled={false}
                  onClick={() => {
                    console.log("descargar tabla de amortizacion...");
                  }}
                />

                <Box sx={{ mt: isMobileOrTablet ? 4 : 4 }}>
                  <FormControlLabel
                    control={<CustomSwitch />}
                    label={
                      <Typography variant={"body2"} color={"primary.dark"}>
                        Declaro, bajo protesta de decir verdad, comprender las condiciones de mi crédito
                      </Typography>
                    }
                    name="confirmDeclaration"
                    checked={authorizeChecked}
                    onChange={(_, checked) => {
                      setAuthorizeChecked(checked);
                    }}
                    disabled={isLoading || isSubmitting}
                  />
                </Box>

                <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                  <Button
                    type="button"
                    variant="default"
                    disabled={isLoading || isSubmitting || !authorizeChecked}
                    onClick={handleContinue}
                  >
                    Continuar
                  </Button>
                </Box>
              </Container>
            </>
          )}
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default BienvenidaFirmaPF;
